import type { Unsubscribe } from './disposables.ts'
import { createLogger } from './logger.ts'

type EventType<Events> = keyof Events & string

/** Called before handlers on every emit; devtools use it to trace events. */
export type EventTrace<Events> = (type: EventType<Events>, payload: unknown) => void

export interface EventBus<Events extends object> {
  on<K extends EventType<Events>>(type: K, handler: (payload: Events[K]) => void): Unsubscribe
  once<K extends EventType<Events>>(type: K, handler: (payload: Events[K]) => void): Unsubscribe
  emit<K extends EventType<Events>>(type: K, payload: Events[K]): void
  setTrace(trace: EventTrace<Events> | null): void
  clear(): void
}

const log = createLogger('event-bus')

/**
 * Typed synchronous event bus. Handlers run in subscription order, and a handler
 * that throws is logged without stopping the others.
 */
export function createEventBus<Events extends object>(): EventBus<Events> {
  // Stored untyped: `on` and `emit` keep each event's handler and payload types in sync.
  type Handler = (payload: unknown) => void
  const handlers = new Map<EventType<Events>, Set<Handler>>()
  let trace: EventTrace<Events> | null = null

  const on: EventBus<Events>['on'] = (type, handler) => {
    let set = handlers.get(type)
    if (!set) {
      set = new Set()
      handlers.set(type, set)
    }
    const untyped = handler as Handler
    set.add(untyped)
    return () => handlers.get(type)?.delete(untyped)
  }

  return {
    on,
    once(type, handler) {
      const unsubscribe = on(type, (payload) => {
        unsubscribe()
        handler(payload)
      })
      return unsubscribe
    },
    emit(type, payload) {
      trace?.(type, payload)
      const set = handlers.get(type)
      if (!set) return
      // Iterate a snapshot so handlers can unsubscribe (or subscribe) mid-emit.
      for (const handler of [...set]) {
        if (!set.has(handler)) continue
        try {
          handler(payload)
        } catch (error) {
          log.error(`Handler for "${type}" failed`, error)
        }
      }
    },
    setTrace(next) {
      trace = next
    },
    clear() {
      handlers.clear()
    },
  }
}
